import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  LightbulbLine,
  LinkLine,
  NewsLine,
  TeamLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Employee} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {KeyboardEvent, MouseEvent, ReactNode, useState} from 'react';

import {frequentPeopleAtom} from './frequentPeople';
import {LinkCalendarHint, meetingRow} from './MeetingViews';
import {PeopleCards} from './PeopleCards';
import {knowledgeSubtitle, newsSubtitle} from './ResultPreviews';
import {COMPANY_ASKS, SearchRow} from './searchRows';
import {TopicChips} from './TopicChips';
import {Label} from './Typography';
import {meetingWhen, useCalendarStatus, useMeetings} from './useMeetings';
import {personRow} from './useResultRows';

/**
 * How someone relates to you, after their name. Frequent only if you've
 * actually opened them; anyone else gets no label, just a face.
 */
const useRelationLabel = () => {
  const {employee, reportingLine, teamMembers, directReports} = useAtomValue(loggedInEmployeeAtom);
  const frequent = useAtomValue(frequentPeopleAtom);
  return (person: Employee): string | undefined =>
    person.ldap === employee.ldap
      ? t('search_box_relation_you')
      : person.ldap === reportingLine[1]?.ldap
        ? t('search_box_relation_manager')
        : directReports.some(({ldap}) => ldap === person.ldap)
          ? t('search_box_relation_report')
          : teamMembers.some(({ldap}) => ldap === person.ldap)
            ? t('search_box_relation_peer')
            : frequent.some(({ldap}) => ldap === person.ldap)
              ? t('search_box_relation_frequent')
              : undefined;
};

/**
 * A row's parts: `lead` is a face or glyph (or, with `wideLead`, a meeting's
 * time), `tag` a quiet word after the title (how a person relates to you,
 * what kind of thing it is).
 */
type RowShape = {
  lead: ReactNode;
  title: string;
  tag?: string;
  meta?: ReactNode;
  wideLead?: boolean;
};

const subtitle = (text: string | null | undefined) => (
  <Text size="small" color="subtle" tagName="div">
    <Truncate>{text}</Truncate>
  </Text>
);

/** A row's lead, title and lines, for any kind of thing. */
export const useRowShape = () => {
  const relation = useRelationLabel();
  return (row: SearchRow): RowShape => {
    switch (row.kind) {
      case 'person':
        return {
          lead: <Avatar user={row.person} size="small" />,
          title: row.person.name,
          tag: relation(row.person),
          meta: subtitle(row.person.role),
        };
      case 'meeting':
        return {
          lead: (
            <div>
              {meetingWhen(row.meeting.start).map((line) => (
                <Text key={line} size="small" color="subtle" tagName="div">
                  {line}
                </Text>
              ))}
            </div>
          ),
          wideLead: true,
          title: row.meeting.title,
          meta: (
            <Split gap="4">
              {row.meeting.attendees
                .filter(({person}) => person)
                .slice(0, 8)
                .map(({key, person}) => (
                  <Split.Item key={key}>
                    <Avatar user={person!} size="small" />
                  </Split.Item>
                ))}
            </Split>
          ),
        };
      case 'team':
        return {
          lead: <UIIcon src={TeamLine} />,
          title: row.team.name ?? t('team'),
          tag: t('team'),
        };
      case 'workstream':
        return {
          lead: <UIIcon src={WorkstreamsLine} />,
          title: row.workstream.name,
          tag: t('workstream'),
        };
      case 'news':
        return {
          lead: <UIIcon src={NewsLine} />,
          title: row.post.title,
          tag: t('news'),
          meta: subtitle(newsSubtitle(row.post)),
        };
      case 'knowledge':
        return {
          lead: <UIIcon src={row.result.kind === 'link' ? LinkLine : LightbulbLine} />,
          title: row.result.title,
          tag: t(row.result.kind === 'link' ? 'search_box_kind_link' : 'search_box_kind_article'),
          meta: subtitle(knowledgeSubtitle(row.result)),
        };
      default:
        return {
          // The section says Ask; the glyph alone marks the row.
          lead: <UIIcon src={CompanyAskGlyph} />,
          title: row.question,
        };
    }
  };
};

/** You, then Frequent, your manager, reports and peers, once each. */
const useCircle = () => {
  const {employee, reportingLine, teamMembers, directReports} = useAtomValue(loggedInEmployeeAtom);
  const frequent = useAtomValue(frequentPeopleAtom);
  const seen = new Set<string>();
  return [
    ...(employee.ldap ? [employee] : []),
    ...frequent,
    ...(reportingLine[1] ? [reportingLine[1]] : []),
    ...directReports,
    ...teamMembers,
  ].filter((person) => person.ldap && !seen.has(person.ldap) && seen.add(person.ldap));
};

const askRow = (question: string): SearchRow => ({
  kind: 'askSuggestion',
  id: `suggestion:${question}`,
  question,
});

/** `count` neighbouring company questions, starting somewhere new each time the box opens. */
const useAskRows = (count: number) => {
  const [pick] = useState(() => Math.floor(Math.random() * COMPANY_ASKS.length));
  return Array.from({length: count}, (_, offset) =>
    askRow(t(COMPANY_ASKS[(pick + offset) % COMPANY_ASKS.length]))
  );
};

/**
 * What the empty box lists: your people, then what to ask, then what's coming
 * up. With nothing coming up, the topic chips take the room instead: all of
 * them, not two lines and "+N" (once the agenda has loaded, so the list
 * doesn't jump under you).
 */
export const useHomeRows = () => {
  const people = useCircle().slice(0, 10);
  const meetingRows = useMeetings().map(meetingRow);
  const {loaded, notLinked} = useCalendarStatus();
  const askRows = useAskRows(2);
  const allChips = loaded && meetingRows.length === 0;
  return {people, askRows, meetingRows, calendarNotLinked: notLinked, allChips};
};
export type HomeRows = ReturnType<typeof useHomeRows>;

/** Everything the arrow keys walk, top to bottom: the cards, then each list. */
export const navOrder = (live: string, results: SearchRow[], home: HomeRows): SearchRow[] =>
  live
    ? results
    : [...home.people.map((person) => personRow(person)), ...home.askRows, ...home.meetingRows];

/**
 * Up and Down in the search field walk `order` from `currentId` (stopping at
 * the ends) and scroll the new row into view; Enter does `onEnter` on the
 * current row.
 */
export const arrowKeys =
  ({
    order,
    currentId,
    onMove,
    onEnter,
  }: {
    order: SearchRow[];
    currentId?: string;
    onMove: (row: SearchRow) => void;
    onEnter: (row: SearchRow) => void;
  }) =>
  (event: KeyboardEvent<HTMLElement>) => {
    const index = order.findIndex(({id}) => id === currentId);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = order[index < 0 ? 0 : Math.min(Math.max(index + step, 0), order.length - 1)];
      if (!next) return;
      onMove(next);
      // Once it has rendered as current, bring it into view (a card scrolls sideways).
      window.requestAnimationFrame(() =>
        document
          .querySelector(`[data-row-id="${CSS.escape(next.id)}"]`)
          ?.scrollIntoView({block: 'nearest', inline: 'nearest'})
      );
    } else if (event.key === 'Enter' && index >= 0) {
      event.preventDefault();
      onEnter(order[index]);
    }
  };

/**
 * The box's left side. Empty: your people (no label), what to ask and the
 * topic chips, then what's coming up. Typed: the results as one group, no
 * label. `group` draws a list of rows and `onPerson` is what a card does;
 * `activeId` marks the card the arrow keys are on.
 */
export const BoxBody = ({
  live,
  results,
  home,
  group,
  onPerson,
  activeId,
}: {
  live: string;
  results: SearchRow[];
  home: HomeRows;
  group: (rows: SearchRow[]) => ReactNode;
  onPerson: (person: Employee, event: MouseEvent) => void;
  activeId?: string;
}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);

  if (live) {
    return results.length > 0 ? group(results) : null;
  }
  return (
    <Stack gap="44">
      {/* No label: a row of faces says what it is. */}
      <PeopleCards
        people={home.people}
        you={employee.ldap}
        activeLdap={activeId?.startsWith('person:') ? activeId.slice('person:'.length) : undefined}
        onOpen={onPerson}
      />
      <Stack gap="12">
        <Label>{t('search_section_ask')}</Label>
        {group(home.askRows)}
        <TopicChips all={home.allChips} />
      </Stack>
      {home.meetingRows.length > 0 ? (
        <Stack gap="12">
          <Label>{t('search_box_coming_up')}</Label>
          {group(home.meetingRows)}
        </Stack>
      ) : (
        home.calendarNotLinked && (
          <Stack gap="12">
            <Label>{t('search_box_coming_up')}</Label>
            <LinkCalendarHint />
          </Stack>
        )
      )}
    </Stack>
  );
};
